<template>
  <sidebar-layout
    v-if="cart.items.length"
    class="view-cart ws-typography"
    :class="{
      'q-pt-2xl q-pb-3xl': desktop,
      'q-pt-md q-pb-2xl': tablet,
      'q-pt-md': mobile,
    }"
    view-title="general.headings.your_cart"
  >
    <cart-items
      xsell
      bordered
      settings-icon-visible
    />
    <ws-message
      v-if="!quickOrderInProgress"
      class="error q-mt-md"
      path="general"
    />
    <div
      v-if="isStandardWorkflow || cart.migrationPromotion.applicable"
      class="row extra-settings"
      :class="{
        'q-mb-sm': cart.migrationPromotion.applicable,
        'q-mt-3xl': desktop,
        'q-mt-xl': tablet,
        'q-mt-lg': mobile,
      }"
    >
      <div class="col">
        <h6 :class="mobile ? 'q-mb-md' : 'q-mb-lg'">
          {{ $t('cart.extra_settings.title') }}
          <span class="text-normal text-small text-ws-base110">({{ $t('uikit:general.optional') }})</span>
        </h6>
        <ws-checkbox
          v-if="isStandardWorkflow"
          v-model="noteReveal"
          :data-test="testNames.noteRevealCheckbox"
          :label="$t('cart.extra_settings.note.label')"
        />

        <transition name="slide-fade-vertical">
          <div
            v-if="noteReveal && isStandardWorkflow"
            class="row q-pl-lg q-py-sm"
          >
            <div class="col">
              <ws-input
                v-model="note"
                type="textarea"
                class="ws-textarea"
                :data-test="testNames.noteInput"
                autogrow
                :placeholder="$t('cart.extra_settings.note.text')"
                debounce="600"
                required
                @input="updateNote"
              />
              <ws-message
                type="alert"
                path="note"
              />
            </div>
          </div>
        </transition>
      </div>
    </div>
    <migration-upload v-if="cart.migrationPromotion.applicable" />

    <template v-slot:side>
      <side-summary>
        <template v-slot>
          <div class="text-center">
            <coupon-auto-insert class="q-mt-lg" />
            <coupon-list
              class="q-mb-sm"
              :class="{
                'q-mt-2xl': desktop,
                'q-mt-md': tablet,
                'q-mt-sm': mobile,
              }"
            />
          </div>
          <quick-order-button class="q-mt-sm" />
        </template>
      </side-summary>
    </template>
  </sidebar-layout>

  <empty-cart v-else />
</template>

<script lang="ts">
// @ts-nocheck -- baseline for pre-existing errors so typecheck can be turned on
import { Component, Vue, Watch } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';
import { get } from 'lodash-es';

import { type CartExtended } from '@/store/const';
import { StoreActions } from '@/store/const.enum';
import store from '@/store';
import { STEPS } from '@/services/const.enum';
import { isStandardWorkflow } from '@/services/config.service';
import {
  deleteNote,
  updateNote,
  reloadCfgAndValidate,
} from '@/services/cart/cart-api.service';
import {
  setEditMode,
  setUserStep,
} from '@/services/order.service';
import { logError } from '@/services/logger.service';
import EmptyCart from './EmptyCart.vue';
import MigrationUpload from '../components/MigrationUpload.vue';
import SidebarLayout from '@/components/SidebarLayout.vue';
import SideSummary from '@/components/SideSummary.vue';
import CartItems from '../components/cart/CartItems.vue';
import CouponList from '@/components/CouponList.vue';
import { scrollToTop } from '@odin/utils';

export const testNames = process.env.NODE_ENV === 'test' ? {
  noteRevealCheckbox: 'note-reveal-checkbox',
  noteInput: 'cart-note-input',
} : {} as Record<string, string>;
import WsMessage from '../../../components/WsMessage.vue';
import QuickOrderButton from '@/components/QuickOrderButton.vue';
import { WsCheckbox, WsInput } from '@odin/components-vue2';
import { type NavigationGuardNext, type Route } from 'vue-router';
import {useCartItemsStore} from '@/store/cartItemsStore';
import { useMessages } from '@odin/services';
import CouponAutoInsert from '@/modules/checkout/components/cart/CouponAutoInsert.vue';

const { deleteMessage, handleUnstructuredError, postMessage } = useMessages();

const FREE_PRODUCTS_LIMIT_REACHED_KEY = 'exception.integrity.free_products_limit_reached';

@Component({
  components: {
    CouponAutoInsert,
    QuickOrderButton,
    WsMessage,
    WsCheckbox,
    WsInput,
    EmptyCart,
    SidebarLayout,
    SideSummary,
    CartItems,
    CouponList,
    MigrationUpload,
  },
  computed: {
    ...mapState(['cart', 'quickOrderInProgress']),
    ...mapGetters(['orderSteps', 'loggedIn', 'cartDomains']),
  },
})
export default class CartView extends Vue {
  readonly cart!: CartExtended;
  readonly orderSteps!: STEPS[];
  readonly loggedIn!: boolean;
  readonly cartDomains!: string[];
  readonly quickOrderInProgress!: boolean;

  noteReveal = false;
  loading = false;
  note: string = '';
  isStandardWorkflow = isStandardWorkflow;
  testNames = testNames;
  cartItemsStore = useCartItemsStore();

  created() {
    deleteMessage('general');
    store.dispatch(StoreActions.SET_USER_STEP, {
      step: STEPS.CART,
    });
    setEditMode(STEPS.CART);
    scrollToTop();
  }

  @Watch('cart.note', { immediate: true })
  cartNoteChanged(note: string, previousNote: string) {
    if (note && !previousNote && !this.loading) {
      this.note = note;
      this.noteReveal = true;
    }
  }

  @Watch('noteReveal')
  cartRevealChanged(noteReveal: boolean) {
    if (!noteReveal) {
      this.note = '';
      this.updateNote('');
    }
  }

  updateNote(note: string) {
    note = note.trim();
    this.loading = true;
    if (note) {
      updateNote(note).finally(() => (this.loading = false));
    } else {
      deleteNote().finally(() => (this.loading = false));
    }
  }

  async beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
    if (!this.cartItemsStore.cartItems.length || !this.loggedIn) {
      // only if there is something to validate or user is not logged in
      return next();
    }

    try {
      await this.validateCart();
      next();
    } catch (error) {
      logError('Cart validation failed', error);
      // ensure user step stays on cart
      await setUserStep(STEPS.CART);
      next(false);
    }
  }

  async validateCart() {
    if (this.cartItemsStore.cartItems.length) {
      try {
        await reloadCfgAndValidate();
      } catch (error) {
        this.handleValidationError(error);
        throw error instanceof Error
          ? error
          : new Error((error as { message?: string })?.message || 'Cart validation failed');
      }
    }
  }

  handleValidationError(error: unknown) {
    const errors = get(error, 'response.data.context.errors');
    const isFreeProductsLimitReached = Array.isArray(errors)
      && errors.some((err) => err?.key === FREE_PRODUCTS_LIMIT_REACHED_KEY);

    if (isFreeProductsLimitReached) {
      deleteMessage('general');
      postMessage('general', this.$t(FREE_PRODUCTS_LIMIT_REACHED_KEY));
    } else {
      handleUnstructuredError(null, error);
    }
  }

  destroyed() {
    deleteMessage('general');
  }
}
</script>

<style lang="scss" scoped>
.view-cart.cmp-sidebar-layout {
  column-gap: map-get($space-4xl, x);

  @media (min-width: $ws-breakpoint-tablet-min) {
    padding-right: map-get($space-sm, x);
    padding-left: map-get($space-md, x);
    column-gap: map-get($space-md, x);
  }

  @media (min-width: $ws-breakpoint-desktop-min) {
    padding-right: map-get($space-md, x);
    column-gap: map-get($space-4xl, x);
  }

  @media (max-width: $ws-breakpoint-mobile-max) {
    ::v-deep {
      > .content {
        padding: 0 map-get($space-sm, x);
      }
    }
  }
}

.drawer-content {
  height: 100%;
}
</style>
